/* ProductSpecPage — standalone (browser-tab) view of the specification sheet.
   Used as a fallback when a user deep-links to /infoshop/product/:pn/spec
   (e.g. they bookmark or paste a URL). Inside UniBuy™ itself, the Spec Sheet
   link opens the SpecSheetModal popup — no new browser window. */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer } from "lucide-react";
import SpecSheetBody from "../components/infoshop/SpecSheetBody";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductSpecPage() {
  const { pn } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/infoshop/products/${pn}`);
        if (!cancelled) setProduct(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Product not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pn]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500" data-testid="spec-loading">Loading specification…</div>
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="spec-error">
          <div className="font-display text-2xl font-semibold text-slate-900">Specification not available</div>
          <div className="mt-2 text-sm text-slate-600">{error || `No specification found for ${pn}.`}</div>
          <Link
            to="/infoshop"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0072CE] hover:underline"
            data-testid="spec-back-to-catalog"
          >
            <ArrowLeft size={14} /> Back to catalog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="product-spec-page">
      {/* Top chrome — hidden in print */}
      <div className="bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/infoshop"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-[#0072CE]"
            data-testid="spec-back-link"
          >
            <ArrowLeft size={14} /> Back to UniBuy™ catalog
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="spec-print-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3.5 py-2"
          >
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <SpecSheetBody product={product} />
      </main>
    </div>
  );
}
